import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Building2, CheckCircle2, CreditCard, Database, FileImage, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const PLANS = [
  { id: 'starter', label: 'Starter', desc: 'Up to 10 users, 1 GB Storage', users: 10, storage: 1024 },
  { id: 'professional', label: 'Professional', desc: 'Up to 50 users, 10 GB Storage', users: 50, storage: 10240 },
  { id: 'enterprise', label: 'Enterprise', desc: 'Unlimited Users, 100 GB Storage', users: 999, storage: 102400 },
];

const RAZORPAY_PAYMENT_URL = 'https://razorpay.me/@s2service';

const RegistrationWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    organizationName: '',
    adminFullName: '',
    workEmail: '',
    phoneNumber: '',
    designation: '',
    organizationEmail: '',
    location: '',
    state: '',
    country: '',
    plan: 'starter',
    transactionId: '',
    paymentScreenshot: null,
  });

  const selectedPlan = PLANS.find(p => p.id === form.plan) || PLANS[0];

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handlePayNow = () => {
    window.open(RAZORPAY_PAYMENT_URL, '_blank', 'noopener,noreferrer');
  };

  const validateStep = () => {
    if (step === 1) {
      const required = ['organizationName', 'adminFullName', 'workEmail', 'phoneNumber', 'designation', 'organizationEmail', 'location', 'state', 'country'];
      return required.every(key => String(form[key] || '').trim());
    }
    if (step === 3) return Boolean(form.paymentScreenshot);
    return true;
  };

  const goNext = () => {
    if (!validateStep()) {
      toast.error(step === 3 ? 'Payment screenshot is required' : 'Please complete all required fields');
      return;
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) {
      toast.error('Payment screenshot is required');
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value) data.append(key, value);
    });

    setLoading(true);
    try {
      await api.post('/auth/register', data);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-lg w-full text-center space-y-5">
          <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Registration Submitted Successfully</h1>
            <p className="text-gray-600 mt-3 leading-6">
              Your details have been submitted successfully.
              <br /><br />
              Your account is currently under review.
              <br /><br />
              Once approved, you will receive an email with a Set Password link and login instructions.
              <br /><br />
              Approval usually takes up to 24 hours.
            </p>
          </div>
          <button onClick={() => navigate('/login')} className="btn-primary mx-auto">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-secondary !py-1.5 !px-3">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Registration</h1>
            <p className="text-gray-500 text-sm">Submit your details for super admin approval</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].map(s => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                step === s ? 'bg-primary-100 text-primary-700' :
                step > s ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center text-xs">{s}</span>}
                {['Organization Details', 'Select Plan', 'Manual Payment', 'Submit'][s - 1]}
              </div>
              {s < 4 && <div className={`flex-1 h-px ${step > s ? 'bg-green-300' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="card space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold">Organization Details</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label">Organization Name *</label>
                  <input className="input" value={form.organizationName} onChange={e => updateField('organizationName', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Admin Full Name *</label>
                  <input className="input" value={form.adminFullName} onChange={e => updateField('adminFullName', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Work Email *</label>
                  <input type="email" className="input" value={form.workEmail} onChange={e => updateField('workEmail', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Phone Number *</label>
                  <input className="input" value={form.phoneNumber} onChange={e => updateField('phoneNumber', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Designation *</label>
                  <input className="input" value={form.designation} onChange={e => updateField('designation', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Organization Email *</label>
                  <input type="email" className="input" value={form.organizationEmail} onChange={e => updateField('organizationEmail', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Location *</label>
                  <input className="input" value={form.location} onChange={e => updateField('location', e.target.value)} required />
                </div>
                <div>
                  <label className="label">State *</label>
                  <input className="input" value={form.state} onChange={e => updateField('state', e.target.value)} required />
                </div>
                <div>
                  <label className="label">Country *</label>
                  <input className="input" value={form.country} onChange={e => updateField('country', e.target.value)} required />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button type="button" onClick={goNext} className="btn-primary">Next: Select Plan</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="card space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold">Select Plan</h2>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {PLANS.map(p => (
                  <label key={p.id} className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    form.plan === p.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="plan" value={p.id} checked={form.plan === p.id} onChange={() => updateField('plan', p.id)} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{p.label}</span>
                        {p.id === 'professional' && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">Popular</span>}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{p.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep(1)} className="btn-secondary">Back</button>
                <button type="button" onClick={goNext} className="btn-primary">Next: Manual Payment</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="card space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold">Manual Payment</h2>
              </div>
              <div className="grid grid-cols-1 gap-6 items-start">
                <div className="space-y-4">
                  <div>
                    <button type="button" onClick={handlePayNow} className="btn-primary w-full sm:w-auto text-base px-8 py-3">
                      Pay Now
                    </button>
                  </div>
                  <div>
                    <label className="label">Transaction ID</label>
                    <input className="input" value={form.transactionId} onChange={e => updateField('transactionId', e.target.value)} placeholder="Optional" />
                  </div>
                  <div>
                    <label className="label">Upload Payment Screenshot *</label>
                    <input type="file" accept="image/*" className="input" onChange={e => updateField('paymentScreenshot', e.target.files?.[0] || null)} required />
                    {form.paymentScreenshot && <p className="text-xs text-gray-500 mt-1">{form.paymentScreenshot.name}</p>}
                  </div>
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary">Back</button>
                <button type="button" onClick={goNext} className="btn-primary">Next: Submit</button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="card space-y-5">
              <div className="flex items-center gap-3">
                <FileImage className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold">Submit Registration Request</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Organization</span><p className="font-medium">{form.organizationName}</p></div>
                <div><span className="text-gray-500">Contact Person</span><p className="font-medium">{form.adminFullName}</p></div>
                <div><span className="text-gray-500">Organization Email</span><p className="font-medium">{form.organizationEmail}</p></div>
                <div><span className="text-gray-500">Selected Plan</span><p className="font-medium">{selectedPlan.label}</p></div>
                <div><span className="text-gray-500">Payment Screenshot</span><p className="font-medium">{form.paymentScreenshot?.name}</p></div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep(3)} className="btn-secondary">Back</button>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default RegistrationWizard;
